import { useCallback, useEffect, useRef } from 'react';
import type { Locale } from '@/strings/types';
import { useShellLocale } from './shellLocale';
import { IconTemplates, IconCheck, IconPlus, IconClose, IconCheckCircle } from './icons';
import TouchDot from './TouchDot';
import { useScrollFade } from './useScrollFade';
import {
  CATALOG,
  CATALOG_TOTAL,
  CATALOG_CATEGORIES,
  CATEGORY_LABEL,
  FORMAT_LABEL,
  HIDDEN_CATEGORIES,
  SECTIONS,
  SECTION_LABEL,
  SYSTEM_CARDS,
  USER_CARDS,
  TEMPLATE_TOTAL,
  PICKED,
  PICKED_SECTIONS,
  NEW_CARD,
  TEMPLATE_CAP,
  useLibraryDemoState,
  type TemplateCard,
} from './useLibraryDemo';
import './LibraryView.css';

/**
 * Tab 5 — the app's Template Library page (/library?tab=in_app) and its
 * "New Template" composer.
 *
 * Ported from MoMP frontend/src/pages/TemplateLibrary.tsx and
 * components/CreateUserTemplateModal.tsx, keeping the parts that carry the
 * tab's claim and dropping the ones a sixteen-second look cannot bank:
 *
 *   KEPT   the stocked card grid, the origin badge, the detail rail with its
 *          section list and "Use this template", the N / 30 counter,
 *          "+ New Template", the composer's catalog rail and ordered section
 *          list, the per-section display format, and Cancel / Save & Publish.
 *   DROPPED the three-tab row (owner, 2026-09-07: "remove the 3 tabs so we keep
 *          this clean" — the composer shows the catalog anyway, and My
 *          Templates is the same shelf filtered), search, the composer's
 *          Description / Version / Notes fields, drag handles, and every
 *          empty / loading / error state.
 *
 * Copy is inline for the same reason AppShell's is: product chrome in a mockup,
 * not site copy. English only until the AR pass. Content policy, the catalog's
 * provenance, and the note that categories run AHEAD of the shipped UI all live
 * in useLibraryDemo.
 */

interface LibraryCopy {
  title: string;
  sub: string;
  counter: (n: number) => string;
  newBtn: string;
  groupSystem: string;
  groupUser: string;
  sectionsN: (n: number) => string;
  sectionsLead: string;
  status: string;
  use: string;
  edit: string;
  userPill: string;
  dialogTitle: string;
  catalog: string;
  breadth: (items: number, cats: number) => string;
  more: (n: number) => string;
  nameLabel: string;
  sectionsLabel: string;
  reorder: string;
  cancel: string;
  publish: string;
}

const COPY: Record<Locale, LibraryCopy> = {
  en: {
    title: 'Template Library',
    sub: 'Start from a ready-made template, or compose your own.',
    counter: (n) => `${n} / ${TEMPLATE_CAP} templates used`,
    newBtn: 'New Template',
    groupSystem: 'Ready-made templates',
    groupUser: 'Your templates',
    sectionsN: (n) => `${n} sections`,
    sectionsLead: 'SECTIONS',
    status: 'Available for processing',
    use: 'Use this template',
    edit: 'Edit',
    userPill: 'USER',
    dialogTitle: 'New Template',
    catalog: 'CATALOG ITEMS',
    breadth: (items, cats) => `${items} sections · ${cats} categories`,
    more: (n) => `+${n} more categories`,
    nameLabel: 'Name',
    sectionsLabel: 'Sections',
    reorder: '(drag to reorder)',
    cancel: 'Cancel',
    publish: 'Save & Publish',
  },
  ar: {
    title: 'مكتبة القوالب',
    /* Two verbs in ONE prose sentence, which §3 allows — its ban is on the
       repeated, listed or interface-level imperative. «قالبك» rather than
       «قالبا خاصا بك»: §4's single most-named padding. */
    sub: 'ابدأ من قالب جاهز أو أنشئ قالبك.',
    counter: (n) => `${n} من ${TEMPLATE_CAP} قالبا`,
    /* مصدر + اسم (§14), not the bare noun phrase the EN button uses: an Arabic
       action button names what it does to what. */
    newBtn: 'إنشاء قالب',
    groupSystem: 'قوالب جاهزة',
    groupUser: 'قوالبك',
    /* Real Arabic number agreement, which the EN plural does not have to think
       about: 3 to 10 take جمع القلة («8 أقسام»), 11 and up take the singular
       accusative («12 قسما»). The cards run 5 to 12, so BOTH forms appear in one
       grid — a single hardcoded plural would be wrong on roughly half of them. */
    sectionsN: (n) => (n <= 10 ? `${n} أقسام` : `${n} قسما`),
    sectionsLead: 'الأقسام',
    status: 'متاح للمعالجة',
    /* §4 strips demonstratives from labels: «استخدام القالب», never «هذا القالب». */
    use: 'استخدام القالب',
    edit: 'تحرير',
    /* NOT «مستخدم». Undiacritized it is both مُستخدِم (a user) and مُستخدَم
       (used) — and on a template card, under a counter that already says
       «11 من 30 قالبا», "used" is the first reading and it is the wrong one. */
    userPill: 'من إنشائك',
    dialogTitle: 'قالب جديد',
    catalog: 'عناصر الفهرس',
    /* 52 crosses into the singular-accusative band; 6 stays in جمع القلة. */
    breadth: (items, cats) => `${items} قسما · ${cats} فئات`,
    /* The hidden count is exactly 2, and §7 rules that 2 is carried by the DUAL
       and never written as a digit — «فئتان», never «2 فئة». Guarded rather than
       assumed, since the arithmetic could change. */
    more: (n) => (n === 2 ? 'وفئتان أخريان' : `و${n} فئات أخرى`),
    nameLabel: 'الاسم',
    sectionsLabel: 'الأقسام',
    /* مصدر, not «اسحب». This is exactly the interface-level imperative §3 bans
       — the ban the `sub` above is a documented exception to, because that one
       is prose. */
    reorder: '(إعادة الترتيب بالسحب)',
    cancel: 'إلغاء',
    /* «&» spelled out — §11: a screen reader mangles the ampersand. */
    publish: 'حفظ ونشر',
  },
};

/* No `useT()` shorthand here, unlike ActionsView: every component on this page
   needs the locale itself as well, to index CATALOG / SYSTEM_CARDS / the label
   maps, so each takes it once and reads COPY off it. */

/* The picked card's element, shared with the cursor. A module-level ref rather
   than a context value: the dot lives in the shell's `overlay`, a different
   subtree, and threading one DOM node through a provider that already carries
   the whole script would be a second channel for one node. Safe because a hero
   mounts exactly one library tab at a time — the view unmounts with the tab. */
const pickedRef: { current: HTMLLIElement | null } = { current: null };

function Card({
  card,
  fresh,
  selected,
  innerRef,
}: {
  card: TemplateCard;
  fresh?: boolean;
  selected?: boolean;
  innerRef?: React.Ref<HTMLLIElement>;
}) {
  const locale = useShellLocale();
  const t = COPY[locale];
  return (
    <li
      ref={innerRef}
      className="pnlib__card"
      data-fresh={fresh}
      data-selected={selected}
    >
      {/* The name gets the card's FULL width and the pill drops to the row
          below. Side by side, the pill took ~90px off the measure and seven of
          eleven names hit the two-line clamp and truncated — "Executive Summary
          Minutes" lost the word that says which template it is. */}
      <span className="pnlib__card-name">{card.name}</span>
      <span className="pnlib__card-foot">
        {/* The system card's pill names its CATEGORY; the user card's names its
            origin. See the Category note in useLibraryDemo for why. */}
        <span
          className="pnlib__pill"
          data-origin={card.origin}
          data-cat={card.category}
        >
          {/* data-* above take the KEYS; only the text translates. Rendering
              `card.category` directly, as this did, is what would have painted
              every Arabic pill in the fallback tone. */}
          {card.category ? CATEGORY_LABEL[locale][card.category] : t.userPill}
        </span>
        <span className="pnlib__card-n">{t.sectionsN(card.sections)}</span>
      </span>
    </li>
  );
}

/* ── The page ────────────────────────────────────────────────────────────── */

export default function LibraryView() {
  const demo = useLibraryDemoState();
  const locale = useShellLocale();
  const t = COPY[locale];
  const systemCards = SYSTEM_CARDS[locale];
  const userCards = USER_CARDS[locale];
  const phase = demo?.phase ?? 'browse';
  const saved = phase === 'saved';
  const inspecting = phase === 'inspect';
  const used = TEMPLATE_TOTAL + (saved ? 1 : 0);
  const picked = systemCards[PICKED]!;
  const shelvesRef = useRef<HTMLDivElement>(null);
  const railListRef = useRef<HTMLOListElement>(null);
  useScrollFade(shelvesRef);
  useScrollFade(railListRef);

  /* Bring the new template into view when it lands.
     Eleven cards in two headed groups do not fit the shelf below ~900px of
     container, so the group that receives the new card — the LAST one — is
     partly below the fold there, and the beat the whole tab builds towards
     would have played off screen. Every alternative cost more than it saved:
     hiding cards at narrow widths thins exactly the mix the grid exists to
     show, and moving the empty cell to the top puts a dashed hole above the
     content. Scrolling is also just what the app does after a save. */
  useEffect(() => {
    if (!saved) return;
    const el = shelvesRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [saved]);

  return (
    <div className="pnlib" data-inspecting={inspecting}>
      {/* The counter and New Template ride ALONGSIDE the title rather than on
          their own row below it (owner, 2026-09-07). See .pnlib__top — it is
          38px of the vertical budget, and the shelf is what gets it. */}
      <div className="pnlib__top">
        <header className="pnlib__head">
          <div className="pnlib__titlerow">
            <span className="pnlib__headicon">
              <IconTemplates size={19} />
            </span>
            <h2 className="pnlib__title">{t.title}</h2>
          </div>
          <p className="pnlib__headsub">{t.sub}</p>
        </header>

        <div className="pnlib__toolbar">
          <span className="pnlib__counter">{t.counter(used)}</span>
          {/* Stays lit while its dialog is open, which is what ties the button
              to the surface it opened. */}
          <span className="pnlib__new" data-pressed={phase === 'compose'} data-dot="new">
            <IconPlus size={13} />
            {t.newBtn}
          </span>
        </div>
      </div>

      <div className="pnlib__body">
        {/* pk-scroll, not the browser default: primitives.css §5.10 ships the
            app's own thin scrollbar and this mockup is meant to read as the app
            (owner, 2026-09-07). */}
        <div className="pnlib__shelves pk-scroll" ref={shelvesRef}>
          <div className="pnlib__grouplabel">{t.groupSystem}</div>
          <ul className="pnlib__grid">
            {systemCards.map((card, i) => (
              <Card
                key={card.name}
                card={card}
                selected={inspecting && i === PICKED}
                innerRef={i === PICKED ? pickedRef : undefined}
              />
            ))}
          </ul>

          {/* The hairline (owner). Two headed groups rather than one sorted run,
              which is what lets the system pills carry a category instead of
              repeating the word "system" on every card. */}
          <div className="pnlib__rule" />

          <div className="pnlib__grouplabel">{t.groupUser}</div>
          <ul className="pnlib__grid">
            {userCards.map((card) => (
              <Card key={card.name} card={card} />
            ))}
            {/* The empty cell is deliberate and it lives in the USER group,
                where a template you just made belongs. Appending instead would
                reflow the group on the one beat meant to draw the eye to one
                card. */}
            {saved ? (
              <Card card={NEW_CARD[locale]} fresh />
            ) : (
              <li className="pnlib__slot" />
            )}
          </ul>
        </div>

        {/* The detail rail, mounted only while a card is picked — the app does
            the same, because a "pick a template…" placeholder floating beside a
            full grid is worse than no rail at all. */}
        {inspecting ? (
          <aside className="pnlib__rail">
            <div className="pnlib__rail-head">
              <span className="pnlib__rail-name">{picked.name}</span>
              <span className="pnlib__pill" data-origin="SYSTEM" data-cat={picked.category}>
                {picked.category ? CATEGORY_LABEL[locale][picked.category] : null}
              </span>
            </div>
            <p className="pnlib__rail-status">
              <IconCheckCircle size={13} />
              {t.status}
            </p>
            <div className="pnlib__rail-count">
              {t.sectionsLead} {picked.sections}
            </div>
            {/* THE ONE SURFACE HERE THAT SCROLLS BY DESIGN. Twelve rows plus a
                head and a pinned footer do not fit the rail below ~640px of
                frame, and the fix is not to pick a thinner template — twelve
                sections is what makes "ready-made" mean something. The footer
                is outside this box so the button never leaves. */}
            <ol className="pnlib__rail-list pk-scroll" ref={railListRef}>
              {PICKED_SECTIONS[locale].map((name, i) => (
                <li key={name} className="pnlib__rail-row">
                  <span className="pnlib__rail-n">{i + 1}</span>
                  <span className="pnlib__rail-sec">{name}</span>
                </li>
              ))}
            </ol>
            <div className="pnlib__rail-foot">
              <span className="pnlib__use">{t.use}</span>
              <span className="pnlib__editbtn">{t.edit}</span>
            </div>
          </aside>
        ) : null}
      </div>
    </div>
  );
}

/* ── The cursor ──────────────────────────────────────────────────────────── */

/**
 * Tab 5 had NO dot at all until 2026-09-07, so its two presses — picking a
 * template and opening the composer — happened with nothing on screen to say
 * where the gesture was (owner: "the user doesn't have a sense where the cursor
 * or touch tracking is choosing or picking"). The card would just select
 * itself.
 */
export function LibraryTouchDot() {
  const demo = useLibraryDemoState();

  const resolve = useCallback((): HTMLElement | null => {
    if (!demo) return null;
    const { aim } = demo;
    if (aim === null) return null;
    switch (aim) {
      case 'card':
        return pickedRef.current;
      case 'new':
        return document.querySelector<HTMLElement>('[data-dot="new"]');
      case 'publish':
        return document.querySelector<HTMLElement>('.pnlib__publish');
      default:
        /* `item:<key>` — a catalog row. Selected by the attribute rather than
           an index, so it survives the rail rendering a different slice. */
        return document.querySelector<HTMLElement>(
          `[data-dot="${CSS.escape(aim)}"]`
        );
    }
  }, [demo]);

  /* The catalog rail scrolls, and eight picks walk down it. A control being in
     the DOM is not the same as being on screen — the lesson tab 4 paid for at
     376, where its whole edit beat played inside clipped overflow. */
  const scroller = useCallback(
    () => document.querySelector<HTMLElement>('.pnlib__cats'),
    []
  );

  if (!demo) return null;
  /* Wrapped so the layer can be raised above the dialog scrim without touching
     TouchDot, which tabs 3 and 4 also render. */
  return (
    <div className="pnlib-dot">
      <TouchDot
        aim={demo.aim}
        resolve={resolve}
        scroller={scroller}
        visible={demo.dotVisible}
        pressing={demo.pressing}
      />
    </div>
  );
}

/* ── The composer ────────────────────────────────────────────────────────── */

/* Rendered into AppShell's `overlay` slot rather than beside the page, so it
   scrims the sidebar and the top bar the way the app's modal does. A dialog
   that dimmed only the content column would read as a panel, not a modal. */
export function LibraryDialog() {
  const demo = useLibraryDemoState();
  const locale = useShellLocale();
  const t = COPY[locale];
  const catsRef = useRef<HTMLUListElement>(null);
  const sectionsRef = useRef<HTMLOListElement>(null);
  /* Before the early return, or the hook count changes between renders. */
  useScrollFade(catsRef);
  useScrollFade(sectionsRef);
  if (!demo || demo.phase !== 'compose') return null;

  const { placed, publishable } = demo;
  /* Keys, not names. This was a Set of DISPLAY strings compared against the
     rail's item names, which ties the tick to two translations agreeing word for
     word — and fails silently, leaving the catalog un-ticked for the whole
     compose beat, if they ever do not. */
  const ticked = new Set(SECTIONS.slice(0, placed).map((s) => s.key));

  return (
    <div className="pnlib-dlg-scrim" aria-hidden="true">
      <div className="pnlib-dlg">
        <div className="pnlib-dlg__head">
          <span className="pnlib-dlg__title">{t.dialogTitle}</span>
          <span className="pnlib-dlg__counter">{t.counter(TEMPLATE_TOTAL)}</span>
          <span className="pnlib-dlg__x">
            <IconClose size={13} />
          </span>
        </div>

        <div className="pnlib-dlg__body">
          <aside className="pnlib__catalog">
            <div className="pnlib__catalog-head">
              <span className="pnlib__catalog-label">{t.catalog}</span>
              <span className="pnlib__catalog-count">
                {t.breadth(CATALOG_TOTAL, CATALOG_CATEGORIES)}
              </span>
            </div>

            <ul className="pnlib__cats pk-scroll" ref={catsRef}>
              {CATALOG[locale].map((cat) => (
                <li key={cat.name} className="pnlib__cat">
                  <div className="pnlib__cat-head">
                    <span className="pnlib__cat-name">{cat.name}</span>
                    <span className="pnlib__cat-n">{cat.count}</span>
                  </div>
                  <ul className="pnlib__items">
                    {cat.items.map((item) => {
                      const on = item.key !== undefined && ticked.has(item.key);
                      return (
                        /* data-dot is what the cursor aims at. Keyed, not
                           name-keyed: the aim has to resolve to the same row in
                           both locales. Rows the template never picks carry no
                           key and are therefore never targeted. */
                        <li
                          key={item.name}
                          className="pnlib__item"
                          data-picked={on}
                          data-dot={item.key ? `item:${item.key}` : undefined}
                        >
                          <span className="pnlib__item-mark">
                            {on ? <IconCheck size={12} /> : <IconPlus size={12} />}
                          </span>
                          <span className="pnlib__item-name">{item.name}</span>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>

            <p className="pnlib__more">{t.more(HIDDEN_CATEGORIES)}</p>
          </aside>

          <section className="pnlib__compose">
            <div className="pnlib__field">
              <span className="pnlib__label">{t.nameLabel}</span>
              <span className="pnlib__input">{NEW_CARD[locale].name}</span>
            </div>

            <div className="pnlib__seclabel">
              {t.sectionsLabel} <em>{t.reorder}</em>
            </div>

            <ol className="pnlib__sections pk-scroll" ref={sectionsRef}>
              {SECTIONS.slice(0, placed).map((s, i) => (
                <li key={s.key} className="pnlib__section">
                  <span className="pnlib__section-n">{i + 1}</span>
                  <span className="pnlib__section-name">{SECTION_LABEL[locale][s.key]}</span>
                  {/* The format chip is the app's own word for how the section
                      renders, so it translates like any other label. */}
                  <span className="pnlib__fmt">{FORMAT_LABEL[locale][s.format]}</span>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <div className="pnlib-dlg__foot">
          <span className="pnlib__cancel">{t.cancel}</span>
          {/* Quiet until every section is in, then primary. The state change IS
              the message: a template becomes publishable when it has a shape. */}
          <span className="pnlib__publish" data-ready={publishable}>
            {t.publish}
          </span>
        </div>
      </div>
    </div>
  );
}
